import React, { useCallback } from "react";
import type { CreditCard } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

export const CardsPage: React.FC = () => {
  const { client } = useAuth();

  const fetchCardBrands = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/card-brands?q=${encodeURIComponent(query)}&take=100&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string }>;
      };
      return payload.data.map((item) => ({ id: item.id, label: item.name }));
    },
    [client]
  );

  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=50&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; agency: string; accountNumber: string; bank?: { name: string } | null }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: item.bank
          ? `${item.bank.name} — ag ${item.agency} / cc ${item.accountNumber}`
          : `ag ${item.agency} / cc ${item.accountNumber}`,
      }));
    },
    [client]
  );

  return (
    <CrudPage<CreditCard>
      title="Cartoes de credito"
      endpoint="/modules/cards"
      searchPlaceholder="Buscar por nome ou bandeira..."
      columns={[
        { label: "Cartao", render: (item) => item.name },
        { label: "Bandeira", render: (item) => item.brand ?? "-" },
        { label: "Final", render: (item) => item.last4 ?? "-" },
        { label: "Fechamento", render: (item) => `Dia ${item.statementDay}` },
        { label: "Vencimento", render: (item) => `Dia ${item.dueDay}` },
        { label: "Ativo", render: (item) => (item.isActive ? "Sim" : "Nao") },
      ]}
      formFields={[
        { name: "name", label: "Nome do Cartão", type: "text", required: true },
        {
          name: "brandId",
          label: "Bandeira",
          type: "select",
          required: true,
          options: [],
          fetchOptions: fetchCardBrands,
        },
        {
          name: "bankAccountId",
          label: "Conta bancária vinculada (para lançamentos na movimentação)",
          type: "select",
          options: [],
          fetchOptions: fetchBankAccounts,
        },
        { name: "last4", label: "Últimos 4 dígitos", type: "text", placeholder: "Ex: 1234" },
        {
          name: "statementDay",
          label: "Dia de Fechamento (1-30)",
          type: "number",
          required: true,
          placeholder: "Ex: 10"
        },
        {
          name: "dueDay",
          label: "Dia de Vencimento (1-30)",
          type: "number",
          required: true,
          placeholder: "Ex: 15"
        },
        {
          name: "limit",
          label: "Limite",
          type: "number",
          placeholder: "Ex: 5000.00"
        },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        name: "",
        brandId: null,
        bankAccountId: null,
        last4: "",
        statementDay: 1,
        dueDay: 10,
        limit: 0,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        name: values.name,
        brandId: values.brandId ? Number(values.brandId) : null,
        bankAccountId: values.bankAccountId ? Number(values.bankAccountId) : null,
        last4: values.last4 || null,
        statementDay: Math.max(1, Math.min(30, Number(values.statementDay))),
        dueDay: Math.max(1, Math.min(30, Number(values.dueDay))),
        limit: values.limit && Number(values.limit) > 0 ? Number(values.limit) : null,
        isActive: values.isActive,
      })}
      permissionWrite="FINANCE_WRITE"
    />
  );
};

